import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Check, ChevronDown, Loader2, Save } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardSubtitle, CardTitle } from '@/components/ui/Card';
import { Textarea } from '@/components/ui/FormField';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/api/client';
import { useClientPrograms, type ClientProgram } from '@/api/clientPrograms';
import {
    useClientProgramTargets,
    type ClientProgramTarget,
} from '@/api/clientProgramTargets';
import {
    useSaveDataPoints,
    useUpdateDataPoint,
    type TargetDataPoint,
} from '@/api/targetDataPoints';
import { TargetDataEntry } from './TargetDataEntry';
import { blankEntry, type EntryState } from './types';

// Strip unscored slots out of a trial-grid valueJson before sending to the
// server. The widget keeps the array at trialsCount length with nulls for
// blank cells; the server only accepts an array of code strings.
function cleanValueJson(vj: EntryState['valueJson']): EntryState['valueJson'] {
    if (vj && 'trials' in vj && Array.isArray((vj as { trials: unknown[] }).trials)) {
        const filtered = ((vj as { trials: unknown[] }).trials)
            .filter((c): c is string => typeof c === 'string' && c.length > 0);
        return { trials: filtered };
    }
    return vj;
}

/*
 * DataCollectionPane — the session-scoped data-collection surface.
 *
 * Flow:
 *   1. Fetch active programs for the session's client.
 *   2. For each program, fetch its active targets.
 *   3. For each target, fetch the existing data point for this session
 *      (if any) so amendments hydrate rather than duplicate-insert.
 *   4. Hold one EntryState per target in local state. Each widget
 *      updates its slot through a stable onChange handler.
 *   5. Save = PATCH for every dirty entry that has an existing DP,
 *      plus one bulk POST for dirty entries that don't yet.
 *
 * Server rules baked in:
 *   - UNIQUE(target, session) means we always PATCH when a DP row
 *     exists, never re-POST. The 409 path is a defensive fallback only.
 *   - valueJson shape is owned per-widget; the server validates.
 *   - One audit row per request — bulk POST + per-DP PATCHes keep the
 *     volume proportional to actual clinical edits.
 *
 * Pane-level unsaved-work coordination:
 *   - TargetCollection reports its dirty count + registers a save-all
 *     handler with DataCollectionInner via the registry context.
 *   - DataCollectionInner aggregates across programs and pushes the
 *     total upward to the parent (SessionPage) through `onDirtyChange`
 *     and writes a save-all function into the caller's `saveAllRef`.
 *   - SessionPage uses those to show a "you have unsaved data" guard
 *     dialog when the user tries to navigate away.
 */

interface CollectionRegistration {
    dirtyCount: number;
    saveAll: () => Promise<void>;
}

interface CollectionRegistry {
    register: (id: string, reg: CollectionRegistration) => void;
    unregister: (id: string) => void;
}

interface DataCollectionPaneProps {
    sessionId: string;
    clientId: string | null;
    /** Disable inputs when the session/note is finalized or read-only. */
    readOnly?: boolean;
    /** Called with the current unsaved-target count whenever it changes. */
    onDirtyChange?: (dirty: number) => void;
    /**
     * Caller-provided ref that the pane populates with a "save every dirty
     * target" function. Throws on the first failure so the caller can
     * decide whether to continue with navigation.
     */
    saveAllRef?: React.RefObject<(() => Promise<void>) | null>;
}

export function DataCollectionPane({ sessionId, clientId, readOnly, onDirtyChange, saveAllRef }: DataCollectionPaneProps) {
    const canCollect = useAuthStore((s) => s.hasPermission('data_collection'));
    const disabled = !!readOnly || !canCollect;

    if (!clientId) {
        return <Alert variant="info">This session isn't tied to a client, so there's no data to collect.</Alert>;
    }

    return (
        <DataCollectionInner
            sessionId={sessionId}
            clientId={clientId}
            disabled={disabled}
            onDirtyChange={onDirtyChange}
            saveAllRef={saveAllRef}
        />
    );
}

function DataCollectionInner({
    sessionId, clientId, disabled, onDirtyChange, saveAllRef,
}: {
    sessionId: string;
    clientId: string;
    disabled: boolean;
    onDirtyChange?: (dirty: number) => void;
    saveAllRef?: React.RefObject<(() => Promise<void>) | null>;
}) {
    const programsQuery = useClientPrograms({ clientId, status: 'Active' });

    // Registry lives at the inner level so it survives across loading/error
    // states; each TargetCollection registers on mount and unregisters on
    // unmount. The ref holds savers for the latest-known state; the state
    // var tracks totalDirty only so we re-render when the count changes.
    const registrationsRef = useRef<Map<string, CollectionRegistration>>(new Map());
    const [totalDirty, setTotalDirty] = useState(0);

    const recompute = useCallback(() => {
        let n = 0;
        for (const r of registrationsRef.current.values()) n += r.dirtyCount;
        setTotalDirty((prev) => (prev === n ? prev : n));
    }, []);

    const registry = useMemo<CollectionRegistry>(() => ({
        register: (id, reg) => {
            registrationsRef.current.set(id, reg);
            recompute();
        },
        unregister: (id) => {
            registrationsRef.current.delete(id);
            recompute();
        },
    }), [recompute]);

    useEffect(() => {
        onDirtyChange?.(totalDirty);
    }, [totalDirty, onDirtyChange]);

    useEffect(() => {
        if (!saveAllRef) return;
        saveAllRef.current = async () => {
            // Sequential so the first failure surfaces and we don't overload
            // the API with parallel writes. Each collection's saveAll raises
            // on error; we let it propagate to the caller.
            for (const r of registrationsRef.current.values()) {
                if (r.dirtyCount > 0) await r.saveAll();
            }
        };
        return () => {
            if (saveAllRef.current) saveAllRef.current = null;
        };
    }, [saveAllRef]);

    if (programsQuery.isLoading) {
        return <div className="flex items-center gap-2 text-sm text-text-muted"><Loader2 className="w-4 h-4 animate-spin" /> Loading programs…</div>;
    }
    if (programsQuery.isError) {
        return <Alert variant="danger">Couldn't load this client's programs.</Alert>;
    }
    const programs = programsQuery.data ?? [];
    if (programs.length === 0) {
        return <Alert variant="info">No active programs for this client. Add programs from the client's Programs tab.</Alert>;
    }

    return (
        <div className="space-y-4">
            {programs.map((program) => (
                <ProgramBlock
                    key={program.id}
                    program={program}
                    sessionId={sessionId}
                    disabled={disabled}
                    registry={registry}
                />
            ))}
        </div>
    );
}

function ProgramBlock({
    program, sessionId, disabled, registry,
}: {
    program: ClientProgram;
    sessionId: string;
    disabled: boolean;
    registry: CollectionRegistry;
}) {
    const targetsQuery = useClientProgramTargets({
        clientProgramId: program.id,
        status: 'In Progress',
    });
    const [expanded, setExpanded] = useState(false);
    // Reported up from TargetCollection so we can surface an "N unsaved" hint
    // when the program is collapsed. The child keeps entries in its own state
    // so we don't unmount it on collapse — CSS-hide instead, state preserved.
    const [dirtyCount, setDirtyCount] = useState(0);

    const targets = targetsQuery.data ?? [];

    if (targetsQuery.isLoading) {
        return (
            <Card>
                <CardTitle>{program.name}</CardTitle>
                <div className="mt-3 flex items-center gap-2 text-sm text-text-muted">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading targets…
                </div>
            </Card>
        );
    }

    if (targets.length === 0) {
        return (
            <Card>
                <CardTitle>{program.name}</CardTitle>
                <CardSubtitle className="mt-1">No in-progress targets for this program.</CardSubtitle>
            </Card>
        );
    }

    return (
        <Card>
            <button
                type="button"
                aria-expanded={expanded}
                onClick={() => setExpanded((v) => !v)}
                className="w-full flex items-start gap-2 text-left"
            >
                <ChevronDown
                    className={cn(
                        'w-4 h-4 mt-1 shrink-0 text-text-subtle transition-transform duration-[var(--duration-base)]',
                        !expanded && '-rotate-90',
                    )}
                />
                <div className="flex-1 min-w-0">
                    <CardTitle>{program.name}</CardTitle>
                    {program.programDomain && <CardSubtitle className="mt-1">{program.programDomain}</CardSubtitle>}
                </div>
                {!expanded && (
                    <span className="text-xs text-text-subtle shrink-0 mt-1">
                        {targets.length} {targets.length === 1 ? 'target' : 'targets'}
                        {dirtyCount > 0 && (
                            <span className="ml-2 text-warning font-medium">· {dirtyCount} unsaved</span>
                        )}
                    </span>
                )}
            </button>
            <div className={cn('mt-3', !expanded && 'hidden')}>
                <TargetCollection
                    programId={program.id}
                    targets={targets}
                    sessionId={sessionId}
                    disabled={disabled}
                    onDirtyChange={setDirtyCount}
                    registry={registry}
                />
            </div>
        </Card>
    );
}

function TargetCollection({
    programId, targets, sessionId, disabled, onDirtyChange, registry,
}: {
    programId: string;
    targets: ClientProgramTarget[];
    sessionId: string;
    disabled: boolean;
    onDirtyChange?: (count: number) => void;
    registry: CollectionRegistry;
}) {
    const qc = useQueryClient();

    // Parallel fetch of the existing DP (if any) for each (target, session)
    // pair. The server doesn't have a "by session" filter, so we fetch each
    // target's points and pick the one matching sessionId. useQueries keeps
    // each lookup independently cached by targetId.
    const dpQueries = useQueries({
        queries: targets.map((t) => ({
            queryKey: ['target-data-points', t.id, '', ''] as const,
            queryFn: () => api.get<TargetDataPoint[]>('/target-data-points', { params: { targetId: t.id } }).then((r) => r.data),
            staleTime: 30_000,
        })),
    });

    const existingByTarget = useMemo(() => {
        const m = new Map<string, TargetDataPoint>();
        dpQueries.forEach((q, i) => {
            const rows = q.data ?? [];
            const hit = rows.find((r) => r.sessionId === sessionId);
            if (hit) m.set(targets[i].id, hit);
        });
        return m;
        // dpQueries references change per render; depending on data is safer.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dpQueries.map((q) => q.dataUpdatedAt).join(','), sessionId]);

    const [entries, setEntries] = useState<Record<string, EntryState>>({});

    // Hydrate local state from the server-side DPs once they're loaded.
    // Runs again if a target list changes — preserves in-progress edits
    // for targets that are still present.
    useEffect(() => {
        setEntries((prev) => {
            const next: Record<string, EntryState> = {};
            for (const t of targets) {
                if (prev[t.id]?.dirty) {
                    next[t.id] = prev[t.id];
                    continue;
                }
                const existing = existingByTarget.get(t.id);
                if (existing) {
                    next[t.id] = {
                        valueNumeric: existing.valueNumeric,
                        valueJson:    existing.valueJson,
                        notes:        existing.notes ?? '',
                        dirty:        false,
                    };
                } else {
                    next[t.id] = prev[t.id] ?? blankEntry();
                }
            }
            return next;
        });
    }, [targets, existingByTarget]);

    const saveBulk = useSaveDataPoints();
    const updateOne = useUpdateDataPoint();
    const [saveError, setSaveError] = useState<string | null>(null);
    const [savingTargetId, setSavingTargetId] = useState<string | null>(null);

    const dirtyCount = Object.values(entries).filter((e) => e.dirty && e.valueNumeric !== null).length;

    // Keep the parent ProgramBlock's dirty count in sync so the collapsed
    // header can show an "N unsaved" hint without needing to lift state.
    useEffect(() => {
        onDirtyChange?.(dirtyCount);
    }, [dirtyCount, onDirtyChange]);

    // Persist an entry to the server. Throws on error so save-all can halt
    // the loop and let the caller react. The single-button handler wraps
    // its own try/catch so it can still surface errors in the inline Alert.
    const persistOne = async (targetId: string) => {
        const entry = entries[targetId];
        if (!entry?.dirty || entry.valueNumeric === null) return;

        // Trial valueJson stores the full (string | null)[] so the grid
        // keeps a stable length in local state; the server only accepts
        // code strings for actually-scored trials. Strip nulls here.
        const cleanedJson = cleanValueJson(entry.valueJson);
        const existing = existingByTarget.get(targetId);

        if (existing) {
            await updateOne.mutateAsync({
                id: existing.id,
                payload: {
                    valueNumeric: entry.valueNumeric,
                    valueJson:    cleanedJson,
                    notes:        entry.notes.trim() || null,
                },
            });
        } else {
            await saveBulk.mutateAsync({
                sessionId,
                points: [{
                    clientProgramTargetId: targetId,
                    valueNumeric:          entry.valueNumeric,
                    valueJson:             cleanedJson ?? undefined,
                    notes:                 entry.notes.trim() || undefined,
                }],
            });
        }
        setEntries((prev) => ({
            ...prev,
            [targetId]: { ...prev[targetId], dirty: false },
        }));
        qc.invalidateQueries({ queryKey: ['target-data-points', targetId] });
    };

    const saveOne = async (targetId: string) => {
        setSaveError(null);
        setSavingTargetId(targetId);
        try {
            await persistOne(targetId);
        } catch (err) {
            const ax = err as AxiosError<{ error?: string }>;
            setSaveError(ax.response?.data?.error ?? 'Failed to save data point.');
        } finally {
            setSavingTargetId(null);
        }
    };

    // Register this collection's saveAll + dirtyCount with the pane-level
    // registry so the unsaved-changes guard can aggregate across programs
    // and trigger a coordinated save-all on navigation.
    useEffect(() => {
        registry.register(programId, {
            dirtyCount,
            saveAll: async () => {
                for (const t of targets) {
                    const e = entries[t.id];
                    if (e?.dirty && e.valueNumeric !== null) {
                        // Re-use persistOne (throws) so save-all aborts on
                        // the first error; the pane-level guard surfaces it
                        // to the user via the blocker dialog.
                        await persistOne(t.id);
                    }
                }
            },
        });
        // persistOne closes over `entries` + `existingByTarget` from this
        // render; re-register on every render so the saved closure is
        // current when the guard fires. No deps = runs every render.
    });

    useEffect(() => {
        return () => registry.unregister(programId);
    }, [programId, registry]);

    return (
        <div className="space-y-4">
            {targets.map((t) => {
                const entry = entries[t.id];
                const isDirty = !!entry?.dirty;
                const isSaved = existingByTarget.has(t.id) && !isDirty;
                const isSavingThis = savingTargetId === t.id;
                const canSave = isDirty && entry?.valueNumeric !== null;
                return (
                <div key={t.id} className="border-t border-border pt-3 first:border-none first:pt-0">
                    <div className="flex items-start justify-between gap-3 mb-2">
                        <div>
                            <div className="text-sm font-semibold text-text">{t.name}</div>
                            <div className="text-xs text-text-subtle capitalize">{t.dataType.replace('_', ' ')}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            {isDirty ? (
                                <span className="text-xs text-warning font-medium inline-flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-warning inline-block" /> Unsaved
                                </span>
                            ) : isSaved ? (
                                <span className="text-xs text-text-subtle inline-flex items-center gap-1">
                                    <Check className="w-3 h-3" /> Saved
                                </span>
                            ) : null}
                            <Button
                                variant="primary"
                                size="xs"
                                onClick={() => saveOne(t.id)}
                                disabled={disabled || !canSave || savingTargetId !== null}
                                aria-label={`Save ${t.name}`}
                            >
                                {isSavingThis ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                Save
                            </Button>
                        </div>
                    </div>
                    <TargetDataEntry
                        target={t}
                        value={entries[t.id] ?? blankEntry()}
                        onChange={(next) => setEntries((prev) => ({ ...prev, [t.id]: next }))}
                        disabled={disabled}
                    />
                    <Textarea
                        className="mt-2 text-xs"
                        rows={2}
                        placeholder="Optional note on this data point"
                        value={entries[t.id]?.notes ?? ''}
                        onChange={(e) => setEntries((prev) => ({
                            ...prev,
                            [t.id]: {
                                ...(prev[t.id] ?? blankEntry()),
                                notes: e.target.value,
                                dirty: true,
                            },
                        }))}
                        disabled={disabled}
                    />
                </div>
                );
            })}

            {saveError && <Alert variant="danger">{saveError}</Alert>}
        </div>
    );
}
